import { Check } from "lucide-react";
import { useEffect, useState } from "react";
import type { FormEvent, ReactNode } from "react";

import {
  ApiError,
  changePassword,
  getOrganization,
  renameOrganization,
  updateMe,
} from "@/api/client";
import type { Licence, Organization } from "@/api/client";
import { useAuth, useCan, useMe } from "@/auth/AuthProvider";
import { Badge } from "@/ui/Badge";
import type { Tone } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { PageHeader } from "@/ui/PageHeader";
import { Card, Skeleton, Spinner } from "@/ui/primitives";

/** The API's `MIN_PASSWORD`. */
const MIN_PASSWORD = 12;

const STATUS: Record<Licence["status"], { label: string; tone: Tone }> = {
  active: { label: "Active", tone: "ok" },
  trial: { label: "Trial", tone: "violet" },
  suspended: { label: "Suspended", tone: "danger" },
};

function failure(err: unknown): string {
  return err instanceof ApiError ? err.detail : "Could not reach the server.";
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="space-y-4 p-5">
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
      {children}
    </Card>
  );
}

function LicenceCard({ licence }: { licence: Licence | null }) {
  if (licence === null) {
    return (
      <p className="text-sm text-muted">
        No licence is recorded yet, so there is no seat limit or end date.
      </p>
    );
  }
  // A suspended licence signs everyone out, so a member only sees one that is live or ended.
  const status = licence.read_only ? null : STATUS[licence.status];
  return (
    <dl className="grid gap-3 text-sm sm:grid-cols-3">
      <div>
        <dt className="text-xs text-muted">Seats</dt>
        <dd className="text-ink">
          {licence.used} of {licence.seats} seats used
        </dd>
      </div>
      <div>
        <dt className="text-xs text-muted">Term</dt>
        <dd className="text-ink">
          {day(licence.starts_at)} to {day(licence.ends_at)}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-muted">Status</dt>
        <dd>
          {status ? (
            <Badge tone={status.tone}>{status.label}</Badge>
          ) : (
            <Badge tone="danger">Ended</Badge>
          )}
        </dd>
      </div>
    </dl>
  );
}

/** The organization's name and its licence (ADR-0012). Owners and admins. */
export function OrganizationScreen() {
  const canRename = useCan("org:manage");
  // The API hides the licence from other roles, so null would read as "none".
  const canSeeLicence = useCan("org:licence:read");
  const [org, setOrg] = useState<Organization | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    getOrganization()
      .then((got) => {
        if (!live) return;
        setOrg(got);
        setName(got.name);
      })
      .catch((err: unknown) => live && setError(failure(err)));
    return () => {
      live = false;
    };
  }, []);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const got = await renameOrganization(name.trim());
      setOrg(got);
      setName(got.name);
      setSaved(true);
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8 lg:px-8">
      <PageHeader
        title="Organization"
        description={canSeeLicence ? "Its name, and what its licence allows." : "Its name."}
      />
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {org === null ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <>
          <Section title="Name">
            <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void save(e)}>
              <label className="min-w-56 flex-1 space-y-1.5">
                <span className="text-xs font-medium text-muted">Name</span>
                <Input
                  value={name}
                  readOnly={!canRename}
                  required
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              {canRename ? (
                <Button
                  type="submit"
                  variant="primary"
                  disabled={busy || !name.trim() || name.trim() === org.name}
                >
                  {busy ? <Spinner className="size-4" /> : null}
                  Save
                </Button>
              ) : null}
              {saved ? (
                <span className="flex items-center gap-1 text-xs text-ok">
                  <Check aria-hidden="true" className="size-3.5" /> Saved
                </span>
              ) : null}
            </form>
          </Section>
          {canSeeLicence ? (
            <Section title="Licence">
              <LicenceCard licence={org.licence} />
            </Section>
          ) : null}
        </>
      )}
    </main>
  );
}

/** Your own name and password. Every member has it, in any licence state. */
export function ProfileScreen() {
  const me = useMe();
  const { adopt } = useAuth();
  const [name, setName] = useState(me.user.name);
  const [nameState, setNameState] = useState<"idle" | "saving" | "saved">("idle");
  const [nameError, setNameError] = useState<string | null>(null);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwDone, setPwDone] = useState(false);

  const saveName = async (event: FormEvent) => {
    event.preventDefault();
    setNameState("saving");
    setNameError(null);
    try {
      adopt(await updateMe(name.trim()));
      setNameState("saved");
    } catch (err) {
      setNameError(failure(err));
      setNameState("idle");
    }
  };

  const savePassword = async (event: FormEvent) => {
    event.preventDefault();
    setPwDone(false);
    if (next.length < MIN_PASSWORD) {
      setPwError(`Choose a password of at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (next !== again) {
      setPwError("The two new passwords are not the same.");
      return;
    }
    setPwBusy(true);
    setPwError(null);
    try {
      await changePassword(current, next);
      setCurrent("");
      setNext("");
      setAgain("");
      setPwDone(true);
    } catch (err) {
      setPwError(failure(err));
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <main className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8 lg:px-8">
      <PageHeader title="Profile" description={me.user.email} />
      <Section title="Name">
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void saveName(e)}>
          <label className="min-w-56 flex-1 space-y-1.5">
            <span className="text-xs font-medium text-muted">Your name</span>
            <Input
              value={name}
              required
              autoComplete="name"
              onChange={(event) => {
                setName(event.target.value);
                setNameState("idle");
              }}
            />
          </label>
          <Button
            type="submit"
            variant="primary"
            disabled={nameState === "saving" || !name.trim() || name.trim() === me.user.name}
          >
            Save name
          </Button>
          {nameState === "saved" ? (
            <span className="flex items-center gap-1 text-xs text-ok">
              <Check aria-hidden="true" className="size-3.5" /> Saved
            </span>
          ) : null}
        </form>
        {nameError ? <Notice tone="danger">{nameError}</Notice> : null}
      </Section>
      <Section title="Password">
        <form className="max-w-sm space-y-4" onSubmit={(e) => void savePassword(e)}>
          {pwError ? <Notice tone="danger">{pwError}</Notice> : null}
          {pwDone ? (
            <Notice tone="ok">Password changed. You were signed out everywhere else.</Notice>
          ) : null}
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted">Current password</span>
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted">New password</span>
            <Input
              type="password"
              autoComplete="new-password"
              required
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted">New password again</span>
            <Input
              type="password"
              autoComplete="new-password"
              required
              value={again}
              onChange={(event) => setAgain(event.target.value)}
            />
          </label>
          <Button type="submit" variant="primary" disabled={pwBusy}>
            {pwBusy ? <Spinner className="size-4" /> : null}
            Change password
          </Button>
        </form>
      </Section>
    </main>
  );
}
